<script setup>
import { ref, computed, onMounted } from 'vue';
import { supabase } from '../db/supabaseClient.js';
import { currentUser } from '../db/auth.js';
import ConfirmModal from '../components/ConfirmModal.vue';
import AlertModal from '../components/AlertModal.vue';
import ChatAssistant from '../components/ChatAssistant.vue';

const user = currentUser();
const isAdmin = computed(() => {
  const role = String(user?.role || "").toLowerCase();
  return role === "admin" || role === "superadmin" || user?.role_id === 1;
});

const purchases = ref([]);
const suppliers = ref([]);
const loading = ref(false);
const showModal = ref(false);
const isEditing = ref(false);

// Modales de alerta y confirmación
const showAlertModal = ref(false);
const alertModalConfig = ref({
  type: 'info',
  title: '',
  message: '',
  autoClose: 0
});

const showConfirmModal = ref(false);
const confirmModalConfig = ref({
  type: 'warning',
  title: '',
  message: '',
  onConfirm: () => {}
});

const form = ref({
  id_base_purchases: null,
  supplier_id: '',
  item_name: '',
  quantity: '',
  unit: 'kg',
  unit_price: '',
  total_price: '',
  purchase_date: new Date().toISOString().split('T')[0],
  notes: ''
});

const units = ['kg', 'g', 'L', 'ml', 'unidad', 'paquete', 'caja'];

const searchQuery = ref('');

const filteredPurchases = computed(() => {
  if (!searchQuery.value.trim()) return purchases.value;
  const query = searchQuery.value.toLowerCase();
  return purchases.value.filter(p =>
    p.item_name.toLowerCase().includes(query) ||
    p.supplier_name?.toLowerCase().includes(query) ||
    p.notes?.toLowerCase().includes(query)
  );
});

onMounted(async () => {
  if (!isAdmin.value) {
    alertModalConfig.value = {
      type: 'error',
      title: 'Acceso Denegado',
      message: 'Solo los administradores pueden acceder a este apartado.',
      autoClose: 0
    };
    showAlertModal.value = true;
    return;
  }
  await loadSuppliers();
  await loadPurchases();
});

async function loadSuppliers() {
  try {
    const { data, error } = await supabase
      .from('suppliers')
      .select('*')
      .order('name');
    
    if (error) throw error;
    suppliers.value = data || [];
  } catch (error) {
    console.error('Error cargando proveedores:', error);
  }
}

async function loadPurchases() {
  loading.value = true;
  try {
    const { data, error } = await supabase
      .from('base_purchases')
      .select(`
        *,
        supplier:suppliers(name)
      `)
      .order('purchase_date', { ascending: false });
    
    if (error) throw error;
    
    purchases.value = (data || []).map(p => ({
      ...p,
      supplier_name: p.supplier?.name || 'Sin proveedor'
    }));
  } catch (error) {
    console.error('Error cargando compras:', error);
    alertModalConfig.value = {
      type: 'error',
      title: 'Error',
      message: 'No se pudieron cargar las compras',
      autoClose: 0
    };
    showAlertModal.value = true;
  } finally {
    loading.value = false;
  }
}

function calculateTotal() {
  const qty = parseFloat(form.value.quantity) || 0;
  const price = parseFloat(form.value.unit_price) || 0;
  form.value.total_price = (qty * price).toFixed(2);
}

function openCreateModal() {
  isEditing.value = false;
  form.value = {
    id_base_purchases: null,
    supplier_id: '',
    item_name: '',
    quantity: '',
    unit: 'kg',
    unit_price: '',
    total_price: '',
    purchase_date: new Date().toISOString().split('T')[0],
    notes: ''
  };
  showModal.value = true;
}

function openEditModal(purchase) {
  isEditing.value = true;
  form.value = {
    id_base_purchases: purchase.id_base_purchases,
    supplier_id: purchase.supplier_id || '',
    item_name: purchase.item_name,
    quantity: purchase.quantity,
    unit: purchase.unit,
    unit_price: purchase.unit_price,
    total_price: purchase.total_price,
    purchase_date: purchase.purchase_date,
    notes: purchase.notes || ''
  };
  showModal.value = true;
}

async function savePurchase() {
  if (!form.value.item_name || !form.value.quantity || !form.value.unit_price) {
    alertModalConfig.value = {
      type: 'warning',
      title: 'Campos requeridos',
      message: 'Por favor completa todos los campos obligatorios',
      autoClose: 0
    };
    showAlertModal.value = true;
    return;
  }

  loading.value = true;
  try {
    const purchaseData = {
      supplier_id: form.value.supplier_id || null,
      item_name: form.value.item_name,
      quantity: parseFloat(form.value.quantity),
      unit: form.value.unit,
      unit_price: parseFloat(form.value.unit_price),
      total_price: parseFloat(form.value.total_price),
      purchase_date: form.value.purchase_date,
      notes: form.value.notes || null,
      created_by: user?.id
    };

      let error;
      if (isEditing.value) {
        const result = await supabase
          .from('base_purchases')
          .update(purchaseData)
          .eq('id_base_purchases', form.value.id_base_purchases);
        error = result.error;
      } else {
      const result = await supabase
        .from('base_purchases')
        .insert([purchaseData]);
      error = result.error;
    }

    if (error) throw error;

    alertModalConfig.value = {
      type: 'success',
      title: isEditing.value ? 'Actualizado' : 'Registrado',
      message: `Compra ${isEditing.value ? 'actualizada' : 'registrada'} exitosamente`,
      autoClose: 2000
    };
    showAlertModal.value = true;

    showModal.value = false;
    await loadPurchases();
  } catch (error) {
    console.error('Error guardando compra:', error);
    alertModalConfig.value = {
      type: 'error',
      title: 'Error',
      message: 'No se pudo guardar la compra',
      autoClose: 0
    };
    showAlertModal.value = true;
  } finally {
    loading.value = false;
  }
}

async function deletePurchase(id_base_purchases) {
  confirmModalConfig.value = {
    type: 'danger',
    title: '¿Eliminar compra?',
    message: 'Esta acción no se puede deshacer',
    onConfirm: async () => {
      loading.value = true;
      try {
        const { error } = await supabase
          .from('base_purchases')
          .delete()
          .eq('id_base_purchases', id_base_purchases);

        if (error) throw error;

        alertModalConfig.value = {
          type: 'success',
          title: 'Eliminado',
          message: 'Compra eliminada exitosamente',
          autoClose: 2000
        };
        showAlertModal.value = true;

        await loadPurchases();
      } catch (error) {
        console.error('Error eliminando compra:', error);
        alertModalConfig.value = {
          type: 'error',
          title: 'Error',
          message: 'No se pudo eliminar la compra',
          autoClose: 0
        };
        showAlertModal.value = true;
      } finally {
        loading.value = false;
      }
    }
  };
  showConfirmModal.value = true;
}

const totalAmount = computed(() => {
  return purchases.value.reduce((sum, p) => sum + parseFloat(p.total_price || 0), 0);
});

// Función para formatear números con separadores de miles (coma para miles, punto para decimales)
function formatNumber(value) {
  if (!value && value !== 0) return '0.00';
  // Formato estadounidense: 1,234,567.89
  return parseFloat(value).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}
</script>

<template>
  <div class="page-container">
    <div class="header">
      <div class="header-content">
        <h1 class="title">
          <span class="material-symbols-rounded icon">inventory_2</span>
          Base de Compras
        </h1>
        <p class="subtitle">Registro de inventario y compras</p>
      </div>
      <button @click="openCreateModal" class="btn-primary">
        <span class="material-symbols-rounded">add</span>
        Nueva Compra
      </button>
    </div>

    <div class="stats-grid">
      <div class="stat-card">
        <span class="material-symbols-rounded stat-icon">shopping_cart</span>
        <div class="stat-content">
          <p class="stat-value">{{ purchases.length }}</p>
          <p class="stat-label">Total Compras</p>
        </div>
      </div>
      <div class="stat-card">
        <span class="material-symbols-rounded stat-icon">payments</span>
        <div class="stat-content">
          <p class="stat-value">${{ formatNumber(totalAmount) }}</p>
          <p class="stat-label">Total Gastado</p>
        </div>
      </div>
    </div>

    <div class="search-bar">
      <span class="material-symbols-rounded">search</span>
      <input
        v-model="searchQuery"
        type="text"
        placeholder="Buscar por producto, proveedor o notas..."
      />
    </div>

    <div v-if="loading && purchases.length === 0" class="loading">
      <span class="material-symbols-rounded spin">progress_activity</span>
      <p>Cargando compras...</p>
    </div>

    <div v-else-if="filteredPurchases.length === 0" class="empty-state">
      <span class="material-symbols-rounded">inventory_2</span>
      <p>{{ searchQuery ? 'No se encontraron compras' : 'No hay compras registradas' }}</p>
      <button @click="openCreateModal" class="btn-secondary">Registrar primera compra</button>
    </div>

    <div v-else class="table-container">
      <table class="modern-table">
        <thead>
          <tr>
            <th>Fecha</th>
            <th>Producto</th>
            <th>Cantidad</th>
            <th>Precio Unit.</th>
            <th>Total</th>
            <th>Proveedor</th>
            <th>Notas</th>
            <th>Acciones</th>
          </tr>
        </thead>
               <tbody>
                 <tr v-for="purchase in filteredPurchases" :key="purchase.id_base_purchases">
                   <td>{{ new Date(purchase.purchase_date).toLocaleDateString('es-ES') }}</td>
                   <td class="product-name">{{ purchase.item_name }}</td>
                   <td>{{ purchase.quantity }} {{ purchase.unit }}</td>
                   <td>${{ formatNumber(purchase.unit_price) }}</td>
                   <td class="total-price">${{ formatNumber(purchase.total_price) }}</td>
                   <td>{{ purchase.supplier_name }}</td>
                   <td class="notes">{{ purchase.notes || '-' }}</td>
                   <td>
                     <div class="action-buttons">
                       <button @click="openEditModal(purchase)" class="btn-icon" title="Editar">
                         <span class="material-symbols-rounded">edit</span>
                       </button>
                       <button @click="deletePurchase(purchase.id_base_purchases)" class="btn-icon danger" title="Eliminar">
                         <span class="material-symbols-rounded">delete</span>
                       </button>
                     </div>
                   </td>
                 </tr>
               </tbody>
      </table>
    </div>

    <!-- Modal -->
    <Teleport to="body">
      <div v-if="showModal" class="modal-overlay" @click="showModal = false">
        <div class="modal-content" @click.stop>
          <div class="modal-header">
            <h2>{{ isEditing ? 'Editar Compra' : 'Nueva Compra' }}</h2>
            <button @click="showModal = false" class="btn-close">
              <span class="material-symbols-rounded">close</span>
            </button>
          </div>

          <form @submit.prevent="savePurchase" class="modal-form">
            <div class="form-row">
              <div class="form-group">
                <label>Producto *</label>
                <input
                  v-model="form.item_name"
                  type="text"
                  placeholder="Ej: Jamón serrano"
                  required
                />
              </div>
              <div class="form-group">
                <label>Proveedor</label>
                <select v-model="form.supplier_id">
                  <option value="">Seleccionar...</option>
                  <option v-for="supplier in suppliers" :key="supplier.id" :value="supplier.id">
                    {{ supplier.name }}
                  </option>
                </select>
              </div>
            </div>

            <div class="form-row">
              <div class="form-group">
                <label>Cantidad *</label>
                <input
                  v-model="form.quantity"
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  @input="calculateTotal"
                  required
                />
              </div>
              <div class="form-group">
                <label>Unidad *</label>
                <select v-model="form.unit">
                  <option v-for="unit in units" :key="unit" :value="unit">{{ unit }}</option>
                </select>
              </div>
              <div class="form-group">
                <label>Precio Unitario *</label>
                <input
                  v-model="form.unit_price"
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  @input="calculateTotal"
                  required
                />
              </div>
            </div>

            <div class="form-row">
              <div class="form-group">
                <label>Total</label>
                <input
                  v-model="form.total_price"
                  type="text"
                  readonly
                  class="readonly"
                />
              </div>
              <div class="form-group">
                <label>Fecha de Compra *</label>
                <input
                  v-model="form.purchase_date"
                  type="date"
                  required
                />
              </div>
            </div>

            <div class="form-group">
              <label>Notas</label>
              <textarea
                v-model="form.notes"
                placeholder="Notas adicionales sobre la compra..."
                rows="3"
              ></textarea>
            </div>

            <div class="modal-actions">
              <button type="button" @click="showModal = false" class="btn-secondary">
                Cancelar
              </button>
              <button type="submit" class="btn-primary" :disabled="loading">
                <span v-if="loading" class="material-symbols-rounded spin">progress_activity</span>
                <span v-else>{{ isEditing ? 'Actualizar' : 'Guardar' }}</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </Teleport>

    <!-- Modales de Alerta y Confirmación -->
    <AlertModal
      :show="showAlertModal"
      :type="alertModalConfig.type"
      :title="alertModalConfig.title"
      :message="alertModalConfig.message"
      :auto-close="alertModalConfig.autoClose"
      @close="showAlertModal = false"
    />

    <ConfirmModal
      :show="showConfirmModal"
      :type="confirmModalConfig.type"
      :title="confirmModalConfig.title"
      :message="confirmModalConfig.message"
      @confirm="confirmModalConfig.onConfirm(); showConfirmModal = false"
      @cancel="showConfirmModal = false"
    />
    <ChatAssistant />
  </div>
</template>

<style scoped>
.page-container {
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
  background: linear-gradient(135deg, #0a0b14 0%, #1a1610 100%);
  min-height: 100vh;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 1rem;
}

.header-content {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.title {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 2rem;
  font-weight: 700;
  color: var(--text);
  margin: 0;
}

.title .icon {
  font-size: 2.5rem;
  color: #7c5cff;
}

.subtitle {
  color: var(--muted);
  margin: 0;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.stat-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.75rem;
  background: linear-gradient(135deg, #7c5cff, #DFC05E);
  border: none;
  border-radius: 20px;
  box-shadow: 0 8px 24px rgba(198, 146, 61, 0.35);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.stat-card::before {
  content: '';
  position: absolute;
  top: -50%;
  right: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
  transition: all 0.5s ease;
}

.stat-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 12px 32px rgba(198, 146, 61, 0.45);
}

.stat-card:hover::before {
  top: -60%;
  right: -60%;
}

.stat-card:nth-child(1) {
  background: linear-gradient(135deg, #f59e0b, #f97316);
}

.stat-card:nth-child(2) {
  background: linear-gradient(135deg, #10b981, #06b6d4);
}

.stat-icon {
  font-size: 3rem;
  color: white;
  background: rgba(255, 255, 255, 0.2);
  padding: 1rem;
  border-radius: 16px;
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 1;
}

.stat-content {
  flex: 1;
  z-index: 1;
}

.stat-value {
  font-size: 2rem;
  font-weight: 800;
  color: white;
  margin: 0 0 0.25rem 0;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.stat-label {
  color: rgba(255, 255, 255, 0.95);
  font-size: 0.95rem;
  margin: 0;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.search-bar {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 12px;
  margin-bottom: 1.5rem;
}

.search-bar span {
  color: var(--muted);
}

.search-bar input {
  flex: 1;
  border: none;
  background: transparent;
  color: var(--text);
  font-size: 1rem;
  outline: none;
}

.table-container {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.modern-table {
  width: 100%;
  border-collapse: collapse;
}

.modern-table thead {
  background: linear-gradient(135deg, rgba(198, 146, 61, 0.1), rgba(223, 192, 94, 0.1));
  border-bottom: 2px solid var(--border);
}

.modern-table th {
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  color: var(--text);
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.modern-table td {
  padding: 1rem;
  border-bottom: 1px solid var(--border);
  color: var(--text);
}

.modern-table tbody tr:hover {
  background: rgba(198, 146, 61, 0.05);
}

.product-name {
  font-weight: 600;
}

.total-price {
  font-weight: 700;
  color: #7c5cff;
}

.notes {
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--muted);
  font-size: 0.9rem;
}

.action-buttons {
  display: flex;
  gap: 0.5rem;
}

.btn-primary, .btn-secondary {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
}

.btn-primary {
  background: linear-gradient(135deg, #7c5cff, #DFC05E);
  color: white;
  box-shadow: 0 4px 12px rgba(198, 146, 61, 0.3);
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(198, 146, 61, 0.4);
}

.btn-secondary {
  background: var(--surface);
  color: var(--text);
  border: 1px solid var(--border);
}

.btn-secondary:hover {
  background: rgba(198, 146, 61, 0.1);
}

.btn-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 8px;
  background: rgba(198, 146, 61, 0.1);
  color: #7c5cff;
  cursor: pointer;
  transition: all 0.3s;
}

.btn-icon:hover {
  background: rgba(198, 146, 61, 0.2);
  transform: scale(1.1);
}

.btn-icon.danger {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}

.btn-icon.danger:hover {
  background: rgba(239, 68, 68, 0.2);
}

.loading, .empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;
  color: var(--muted);
}

.loading span, .empty-state span {
  font-size: 4rem;
  margin-bottom: 1rem;
  color: #7c5cff;
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

.modal-content {
  background: var(--surface);
  border-radius: 24px;
  max-width: 700px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid var(--border);
}

.modal-header h2 {
  margin: 0;
  color: var(--text);
  font-size: 1.5rem;
}

.btn-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: var(--muted);
  cursor: pointer;
  transition: all 0.3s;
}

.btn-close:hover {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}

.modal-form {
  padding: 1.5rem;
}

.form-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-group label {
  font-weight: 600;
  color: var(--text);
  font-size: 0.9rem;
}

.form-group input,
.form-group select,
.form-group textarea {
  padding: 0.75rem 1rem;
  border: 1px solid var(--border);
  border-radius: 12px;
  background: var(--background);
  color: var(--text);
  font-size: 1rem;
  transition: all 0.3s;
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #7c5cff;
  box-shadow: 0 0 0 3px rgba(198, 146, 61, 0.1);
}

.form-group input.readonly {
  background: rgba(198, 146, 61, 0.05);
  font-weight: 600;
  color: #7c5cff;
  cursor: not-allowed;
}

.modal-actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--border);
}

@media (max-width: 768px) {
  .page-container {
    padding: 1rem;
  }

  .header {
    flex-direction: column;
    align-items: stretch;
  }

  .form-row {
    grid-template-columns: 1fr;
  }

  .table-container {
    overflow-x: auto;
  }

  .modern-table {
    min-width: 800px;
  }
}
</style>
